import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * PageWithAside — one column, unless the page has a right-column card to show
 * (Health declutter 2026-09-09, option D; platform-wide 2026-09-10).
 *
 * `aside` null → the children take the full width; otherwise a 300px sticky
 * column opens at `asideAt` and the aside stacks under the content below that.
 * `asideFirstOnMobile` stacks it ABOVE instead — for an aside that is the
 * page's answer (the Finances money tiles) rather than a companion's remark.
 */
export interface PageWithAsideProps {
  aside: ReactNode | null;
  asideAt?: 'lg' | 'xl';
  asideFirstOnMobile?: boolean;
  children: ReactNode;
  className?: string;
}

const GRID: Record<'lg' | 'xl', string> = {
  lg: 'lg:grid lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-8',
  xl: 'xl:grid xl:grid-cols-[minmax(0,1fr)_300px] xl:gap-8',
};
const ASIDE: Record<'lg' | 'xl', string> = {
  lg: 'mt-6 lg:mt-0 lg:sticky lg:top-6 lg:self-start',
  xl: 'mt-6 xl:mt-0 xl:sticky xl:top-6 xl:self-start',
};
const FIRST: Record<'lg' | 'xl', string> = {
  lg: 'order-first mt-0 mb-6 lg:order-none lg:mb-0',
  xl: 'order-first mt-0 mb-6 xl:order-none xl:mb-0',
};

export function PageWithAside({ aside, asideAt = 'lg', asideFirstOnMobile = false, children, className }: PageWithAsideProps) {
  const has = aside !== null && aside !== undefined && aside !== false;
  return (
    <div className={cn(has && GRID[asideAt], has && asideFirstOnMobile && 'flex flex-col', className)}>
      <div className="min-w-0">{children}</div>
      {has && (
        <aside className={cn(ASIDE[asideAt], asideFirstOnMobile && FIRST[asideAt])}>
          {aside}
        </aside>
      )}
    </div>
  );
}
